import React, { useRef } from "react";
import { CheckedInCodeRow } from "src/components/CheckedIn/CheckedInStyles";

interface CodeInputProps {
    code: string;
    disabled: boolean;
    onChange: (code: string) => void;
}

export function CodeInput({ code, disabled, onChange }: CodeInputProps): React.ReactElement {
    const inputs = useRef<Array<HTMLInputElement | null>>([]);

    const enterDigits = (index: number, value: string): void => {
        const digits: string = value.replace(/\D/g, "").slice(0, 6);
        if (!digits) {
            onChange(code.slice(0, index));
            return;
        }
        const start: number = digits.length === 6 ? 0 : Math.min(index, code.length);
        const next: string = (code.slice(0, start) + digits + code.slice(start + digits.length)).slice(0, 6);
        onChange(next);
        inputs.current[Math.min(start + digits.length, 5)]?.focus();
    };

    return (
        <CheckedInCodeRow role="group" aria-label="Six-digit verification code">
            {Array.from({ length: 6 }, (_, index: number) => (
                <input
                    key={index}
                    ref={element => { inputs.current[index] = element; }}
                    aria-label={`Code digit ${index + 1}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    value={code[index] || ""}
                    disabled={disabled}
                    onChange={event => {
                        const insertedText: string | null = (event.nativeEvent as InputEvent).data;
                        enterDigits(index, insertedText ?? event.target.value);
                    }}
                    onFocus={event => event.target.select()}
                    onPaste={event => {
                        event.preventDefault();
                        enterDigits(index, event.clipboardData.getData("text"));
                    }}
                    onKeyDown={event => {
                        if (event.key === "Backspace") {
                            event.preventDefault();
                            const previous: number = code[index] ? index : Math.max(0, index - 1);
                            onChange(code.slice(0, previous));
                            inputs.current[previous]?.focus();
                        } else if (event.key === "ArrowLeft") {
                            inputs.current[Math.max(0, index - 1)]?.focus();
                        } else if (event.key === "ArrowRight") {
                            inputs.current[Math.min(5, index + 1)]?.focus();
                        }
                    }}
                />
            ))}
        </CheckedInCodeRow>
    );
}
